import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import MainLayout from "../../../components/Layout/MainLayout";
import "../../../assets/styles/rdcstatus.css";
import { commonService } from "../../../services/commonService";
import Pagination from '../../../components/Common/Pagination';
import Loader from '../../../components/Common/Loader';
import ProjectDetailOverlay from "../../../components/Common/ProjectDetailOverlay";
import { capitalize } from "../../../utils/formatters";
import FDCSideRail from "../../../components/Common/FDCSideRail";
import FDCCreateWizard from "./FDCCreateWizard";
import { stageChipClass, stageLabel } from "../../../components/Common/StageStepper";
import ColumnFilterDropdown, { ColumnFilterOption } from "../../../components/Common/ColumnFilterDropdown";
import DateRangeFilterDropdown from "../../../components/Common/DateRangeFilterDropdown";
import { hasPermission, isCXUser } from "../../../utils/permissions";
import { BackIcon } from "../../../components/Dashboard/DataCenterIcons";
import taskService, { FDCOverview, OverviewSubtask } from "../../../services/taskService";
import { NEXT_STATUS } from "../../../components/Common/TaskWorkflowPanel";
import { ROUTES } from "../../../utils/constants";
import "../../../assets/styles/Workflow.css";

const slaChipClass = (label?: string) => {
  if (!label) return 'wf-chip2 wf-chip2-neutral';
  if (/overdue/i.test(label)) return 'wf-chip2 wf-chip2-crit';
  if (/met/i.test(label)) return 'wf-chip2 wf-chip2-ok';
  return 'wf-chip2 wf-chip2-neutral';
};

// Main table's Due Date column shows the real date always (not "Met"/
// "Overdue" text) - color still signals status, driven by sla_status
// instead of parsing the label string.
const dueDateChipClass = (status?: string) => {
  if (status === 'overdue') return 'wf-chip2 wf-chip2-crit';
  if (status === 'met') return 'wf-chip2 wf-chip2-ok';
  return 'wf-chip2 wf-chip2-neutral';
};
// Pinned to America/Los_Angeles (PST/PDT) to match ProjectDetailOverlay.tsx's
// formatDate - previously this used the viewer's own browser timezone
// instead, so a due date stored near midnight could land on a different
// calendar day here than in the project detail popup, depending on where the
// viewer's machine happened to be set.
const formatDueDate = (dateStr?: string) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-GB', { timeZone: 'America/Los_Angeles' });
};

const priorityChipClass = (priority?: string | null) => {
  if (priority === 'high') return 'wf-chip2 wf-chip2-crit';
  if (priority === 'low') return 'wf-chip2 wf-chip2-ok';
  return 'wf-chip2 wf-chip2-neutral';
};

const STAGE_OPTIONS = ['pending_build', 'in_progress', 'qa_review', 'ready_to_partially_deliver', 'partially_delivered', 'ready_to_deliver', 'delivered', 'rejected'];

// Same record_type values/labels as the backend's own title-builder
// (webhooks.py's _build_project_title) - "Fulfillment" is the default label
// for anything that isn't explicitly refulfillment/goodwill.
const RECORD_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'fulfillment', label: 'Fulfillment' },
  { value: 'refulfillment', label: 'Re-Fulfillment' },
  { value: 'goodwill', label: 'Goodwill' },
  { value: 'credit_fulfillment', label: 'Credit Fulfillment' },
];
const recordTypeLabel = (value?: string) => RECORD_TYPE_OPTIONS.find((o) => o.value === value)?.label || value || '—';

// "Today"/"Weekly"/"Monthly"/"Year" need today's actual calendar date in
// America/Los_Angeles (not the viewer's own machine timezone) - same pin as
// formatDueDate above - so the quick filters always match what the Created
// At column itself displays, regardless of where the viewer's browser is set.
const todayPST = (): { y: number; m: number; d: number } => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Los_Angeles', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date());
  return {
    y: Number(parts.find((p) => p.type === 'year')!.value),
    m: Number(parts.find((p) => p.type === 'month')!.value),
    d: Number(parts.find((p) => p.type === 'day')!.value),
  };
};
const toDateStr = (y: number, m: number, d: number) =>
  `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

type CreatedAtPreset = 'today' | 'week' | 'month' | 'year' | 'all';
const PRESET_LABELS: Record<CreatedAtPreset, string> = {
  today: 'Today', week: 'Weekly', month: 'Monthly', year: 'Year', all: 'All',
};
const presetRange = (preset: CreatedAtPreset): { from: string; to: string } => {
  const { y, m, d } = todayPST();
  const todayStr = toDateStr(y, m, d);
  if (preset === 'all') return { from: '', to: '' };
  if (preset === 'today') return { from: todayStr, to: todayStr };
  if (preset === 'month') return { from: toDateStr(y, m, 1), to: todayStr };
  if (preset === 'year') return { from: toDateStr(y, 1, 1), to: todayStr };
  // week: Monday-start, same convention as the Overview's "Delivered this week" KPI
  const dow = new Date(y, m - 1, d).getDay(); // 0=Sun..6=Sat
  const daysSinceMonday = dow === 0 ? 6 : dow - 1;
  const monday = new Date(y, m - 1, d - daysSinceMonday);
  return { from: toDateStr(monday.getFullYear(), monday.getMonth() + 1, monday.getDate()), to: todayStr };
};

const FDCPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id: routeProjectId } = useParams<{ id?: string }>();
  const [downloads, setDownloads] = useState<any[]>([]);
  const [pageDetail, setPageDetail] = useState<any>([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(15);

  // Column-header filters - each a list of selected values, ANDed together
  // and applied server-side (see fetchData) so they search/paginate across
  // the whole dataset, not just whatever's currently on screen.
  const [brandFilter, setBrandFilter] = useState<string[]>([]);
  const [recordTypeFilter, setRecordTypeFilter] = useState<string[]>([]);
  const [stageFilter, setStageFilter] = useState<string[]>([]);
  const [assigneeFilter, setAssigneeFilter] = useState<string[]>([]);
  const [createdByFilter, setCreatedByFilter] = useState<string[]>([]);
  const [dueStatusFilter, setDueStatusFilter] = useState<string[]>([]);
  const [deliveredThisWeekFilter, setDeliveredThisWeekFilter] = useState(false);
  const [createdFromFilter, setCreatedFromFilter] = useState('');
  const [createdToFilter, setCreatedToFilter] = useState('');
  // Tracks which quick-filter button (if any) produced the current
  // createdFrom/To range, purely so the matching button can show as active -
  // set to null whenever the range was set some other way (the custom
  // Created At popover, or cleared), so no button looks selected then.
  const [createdAtPreset, setCreatedAtPreset] = useState<CreatedAtPreset | null>(null);
  const [filterOptions, setFilterOptions] = useState<{ brands: string[]; assignees: { id: number; name: string }[]; created_by: { id: number; name: string }[] }>({ brands: [], assignees: [], created_by: [] });

  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [queueFilter, setQueueFilter] = useState<'all' | 'mine' | 'qa' | 'fulfillment' | 'cx'>(
    (location.state as any)?.initialFilter || 'all'
  );
  const [workflowPopup, setWorkflowPopup] = useState<{ open: boolean; item: any }>({ open: false, item: null });
  const [wizardOpen, setWizardOpen] = useState(false);
  const [railOpen, setRailOpen] = useState(false);

  const [overviewData, setOverviewData] = useState<FDCOverview | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [overviewError, setOverviewError] = useState<string | null>(null);

  const refreshKey = (location.state as any)?.refresh;

  useEffect(() => {
    fetchData(1, searchText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsPerPage, queueFilter, refreshKey, brandFilter, recordTypeFilter, stageFilter, assigneeFilter, createdByFilter, dueStatusFilter, deliveredThisWeekFilter, createdFromFilter, createdToFilter]);

  useEffect(() => {
    commonService.fdcFilterOptions()
      .then(setFilterOptions)
      .catch(() => {});
  }, []);

  const loadOverview = () => {
    taskService.getOverview()
      .then(setOverviewData)
      .catch(() => setOverviewError('Failed to load overview'))
      .finally(() => setOverviewLoading(false));
  };

  useEffect(() => {
    loadOverview();
  }, []);

  const openProject = (id: number) => {
    window.open(`${ROUTES.FDC_PROJECTS}/${id}`, '_blank');
  };

  const mainTableRef = useRef<HTMLDivElement>(null);
  const NON_TERMINAL_STAGES = STAGE_OPTIONS.filter((s) => s !== 'delivered' && s !== 'rejected');

  // Drills an Overview KPI card into the All Projects table below, filtered
  // to exactly the set of projects that KPI counted - clearing every other
  // filter first so nothing left over from a previous search narrows the
  // result below the number the person just clicked.
  const applyKpiFilter = (kpi: 'active' | 'at_risk' | 'delivered_this_week') => {
    setSearchText('');
    setQueueFilter('all');
    setBrandFilter([]);
    setRecordTypeFilter([]);
    setAssigneeFilter([]);
    setCreatedByFilter([]);
    setCreatedFromFilter('');
    setCreatedToFilter('');
    setCreatedAtPreset(null);
    setStageFilter(kpi === 'active' ? NON_TERMINAL_STAGES : []);
    setDueStatusFilter(kpi === 'at_risk' ? ['overdue'] : []);
    setDeliveredThisWeekFilter(kpi === 'delivered_this_week');
    mainTableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const toggleSubtask = async (t: OverviewSubtask) => {
    const next = NEXT_STATUS[t.kind]?.[t.status];
    if (!next) return; // already at a terminal status
    await taskService.updateTaskStatus(t.task_id, next);
    loadOverview();
  };

  useEffect(() => {
    const hadInitialFilter = (location.state as any)?.initialFilter;
    if (hadInitialFilter) {
      window.history.replaceState({}, document.title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // /data-centers/fdc/projects/:id (own row click and Overview page links both
  // open this URL in a NEW TAB, via window.open) - the same list page as
  // always, just with this project's popup auto-opened over it, so a
  // freshly-opened tab looks exactly like this page did after clicking that
  // project row directly.
  useEffect(() => {
    if (!routeProjectId) return;
    commonService.fdcList({ project_id: Number(routeProjectId), page: 1, page_size: 1 })
      .then((response: any) => {
        const full = response?.data?.[0];
        if (full) setWorkflowPopup({ open: true, item: full });
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeProjectId]);

  const fetchData = async (newPage: any, search: any) => {
    const payload: any = {
      page: newPage,
      page_size: rowsPerPage,
      search: search
    };
    if (queueFilter === 'mine') payload.assigned_to_me = true;
    if (queueFilter === 'qa') payload.stage = 'qa_review';
    // Fulfillment now owns both partial and final delivery, so their queue
    // needs to catch either "ready" checkpoint, not just the final one -
    // the backend's stage filter accepts a comma-separated list for this.
    if (queueFilter === 'fulfillment') payload.stage = 'ready_to_partially_deliver,ready_to_deliver';
    // CxEx's own queue - both "my turn to look at it" and "sent back,
    // waiting on the BD" so they can track corrections in flight too.
    if (queueFilter === 'cx') payload.stage = 'pending_cx_review,cx_correction_needed';
    // The Stage column filter only applies on the 'all'/'mine' tabs - QA
    // Queue and Fulfillment Queue already force their own stage via the tab.
    if (stageFilter.length > 0 && (queueFilter === 'all' || queueFilter === 'mine')) payload.stage = stageFilter.join(',');

    if (brandFilter.length > 0) payload.brand = brandFilter.join(',');
    if (recordTypeFilter.length > 0) payload.record_type = recordTypeFilter.join(',');
    if (assigneeFilter.length > 0) payload.assignee_ids = assigneeFilter.join(',');
    if (createdByFilter.length > 0) payload.created_by_ids = createdByFilter.join(',');
    if (dueStatusFilter.length > 0) payload.due_status = dueStatusFilter.join(',');
    if (deliveredThisWeekFilter) payload.delivered_this_week = true;
    if (createdFromFilter) payload.created_from = createdFromFilter;
    if (createdToFilter) payload.created_to = createdToFilter;

    try {
      setLoading(true);
      const response = await commonService.fdcList(payload);

      setDownloads(response?.data);
      const page_data = {
        page: response.page,
        total_pages: response.total_pages,
        total_records: response.total,
        page_size: response.page_size,
      };
      setPageDetail(page_data);
      return response?.data;
    } catch (err) {
      setErrorMsg("Failed to load records.");
    } finally {
      setLoading(false);
    }
  };


  const { title: pageTitle, sub: pageSub } = queueFilter === 'qa'
    ? { title: 'QA Queue', sub: 'Projects awaiting quality review before delivery.' }
    : queueFilter === 'fulfillment'
    ? { title: 'Fulfillment Queue', sub: 'Projects ready to deliver to the customer.' }
    : queueFilter === 'cx'
    ? { title: 'CxEx Review Queue', sub: 'Fulfillment projects awaiting Customer Experience approval.' }
    : queueFilter === 'mine'
    ? { title: 'My Projects', sub: 'Projects where you\'re the assigned lead (PC Manager).' }
    : { title: 'FDC Projects', sub: 'Every fulfillment project, one workflow.' };

  return (
    <MainLayout>
      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <FDCSideRail mobileOpen={railOpen} onCloseMobile={() => setRailOpen(false)} />
      <div className="rde-downloads-page" style={{ backgroundColor: "#f5f5f5", flex: 1, overflowY: 'auto', padding: '26px 30px' }}>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <button onClick={() => setRailOpen(true)} className="wf-rail-toggle" aria-label="Open menu">☰</button>
          <button
            onClick={() => navigate((location.state as any)?.fromAdmin ? '/admin/dashboard' : ROUTES.DASHBOARD)}
            className="regular-rdc-back-button"
            style={{ margin: 0 }}
          >
            <BackIcon />
            {(location.state as any)?.fromAdmin ? 'Back to Admin' : 'Back'}
          </button>
        </div>

        {overviewLoading && <div style={{ color: '#6b7280', textAlign: 'center' }}>Loading...</div>}
        {overviewError && <div style={{ color: '#ef4444' }}>{overviewError}</div>}

        {overviewData && (
          <>
            <div className="wf-kpi-row" style={{ marginBottom: 26 }}>
              <div
                className="wf-kpi"
                role="button"
                tabIndex={0}
                onClick={() => applyKpiFilter('active')}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') applyKpiFilter('active'); }}
                title="View these projects"
              >
                <div className="wf-kpi-num">{overviewData.active_projects}</div>
                <div className="wf-kpi-label">Active projects</div>
                <div className="wf-kpi-delta" style={{ color: '#9ca3af' }}>in your view</div>
              </div>
              <div
                className="wf-kpi"
                role="button"
                tabIndex={0}
                onClick={() => applyKpiFilter('at_risk')}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') applyKpiFilter('at_risk'); }}
                title="View these projects"
              >
                <div className="wf-kpi-num" style={{ color: overviewData.at_risk_slas > 0 ? '#ef4444' : 'inherit' }}>
                  {overviewData.at_risk_slas}
                </div>
                <div className="wf-kpi-label">At-risk SLAs</div>
                <div className="wf-kpi-delta" style={{ color: overviewData.at_risk_slas > 0 ? '#ef4444' : '#10b981' }}>
                  {overviewData.at_risk_slas > 0 ? 'needs attention' : 'all on track'}
                </div>
              </div>
              <div
                className="wf-kpi"
                role="button"
                tabIndex={0}
                onClick={() => applyKpiFilter('delivered_this_week')}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') applyKpiFilter('delivered_this_week'); }}
                title="View these projects"
              >
                <div className="wf-kpi-num" style={{ color: '#10b981' }}>{overviewData.delivered_this_week}</div>
                <div className="wf-kpi-label">Delivered this week</div>
                <div className="wf-kpi-delta" style={{ color: '#10b981' }}>on target</div>
              </div>
            </div>

            <div className="wf-section-title">
              <h3>My open subtasks</h3>
            </div>
            <div className="wf-card wf-tablewrap wf-fdc-table" style={{ marginBottom: 26, maxHeight: 340, overflowY: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th></th>
                    <th>Task</th>
                    <th>Project</th>
                    <th>Brand</th>
                    <th>Assignee</th>
                    <th>Created By</th>
                    <th>Created At</th>
                    <th>Priority</th>
                    <th>Due Date</th>
                    <th>Stage</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {overviewData.my_open_subtasks.length === 0 ? (
                    <tr><td colSpan={11}><div className="wf-empty-hint">Nothing delegated to you right now.</div></td></tr>
                  ) : (
                    overviewData.my_open_subtasks.map((t) => {
                      const isTerminal = !NEXT_STATUS[t.kind]?.[t.status];
                      return (
                        <tr key={t.task_id}>
                          <td>
                            <div
                              className={`wf-subtask-check ${isTerminal ? 'done' : ''}`}
                              onClick={() => toggleSubtask(t)}
                              style={{ cursor: isTerminal ? 'default' : 'pointer' }}
                            >
                              {isTerminal ? '✓' : ''}
                            </div>
                          </td>
                          <td data-label="Task" style={{ fontWeight: 600 }}>{t.title || (t.kind === 'qa' ? 'QC the list' : t.kind === 'deliver' ? 'Deliver to customer' : t.kind === 'other' ? 'Other subtask' : 'Build subtask')}</td>
                          <td data-label="Project">
                            <div style={{ fontWeight: 400, fontSize: 12 }}>{t.project_summary ? capitalize(t.project_summary) : t.project_summary}</div>
                            <div className="wf-mono" style={{ fontSize: 11, color: '#9ca3af' }}>{t.projectid}</div>
                          </td>
                          <td data-label="Brand">{t.brand_name || '—'}</td>
                          <td data-label="Assignee">{t.project_assignee_name || <span style={{ color: '#9ca3af' }}>Unassigned</span>}</td>
                          <td data-label="Created By">{t.project_created_by_name || '—'}</td>
                          <td data-label="Created At">{t.project_created_at ? new Date(t.project_created_at).toLocaleDateString('en-GB', { timeZone: 'America/Los_Angeles' }) : '—'}</td>
                          <td data-label="Priority"><span className={priorityChipClass(t.priority)}>{t.priority ? t.priority.charAt(0).toUpperCase() + t.priority.slice(1) : 'Medium'}</span></td>
                          <td data-label="Due Date"><span className={slaChipClass(t.sla_label)}>{t.sla_label || '—'}</span></td>
                          <td data-label="Stage"><span className={stageChipClass(t.project_stage)}>{stageLabel(t.project_stage)}</span></td>
                          <td>
                            <button className="wf-btn" onClick={() => openProject(t.project_id)}>
                              Open →
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        <div ref={mainTableRef} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 22 }}>
          <div>
            <h1 style={{ fontSize: 22, marginBottom: 4 }}>{pageTitle}</h1>
            <div style={{ fontSize: 12, color: '#9ca3af' }}>{pageSub}</div>
          </div>
          {hasPermission('fdc_project:create') && (
            <button className="wf-btn wf-btn-primary" onClick={() => setWizardOpen(true)}>+ New Project</button>
          )}
        </div>

        <div className="wf-queue-toggle">
          <button className={queueFilter === 'all' ? 'active' : ''} onClick={() => setQueueFilter('all')}>All Projects</button>
          <button className={queueFilter === 'mine' ? 'active' : ''} onClick={() => setQueueFilter('mine')}>My Projects</button>
          {hasPermission('fdc_task:qa_review') && (
            <button className={queueFilter === 'qa' ? 'active' : ''} onClick={() => setQueueFilter('qa')}>QA Queue</button>
          )}
          {hasPermission('fdc_project:deliver') && (
            <button className={queueFilter === 'fulfillment' ? 'active' : ''} onClick={() => setQueueFilter('fulfillment')}>Fulfillment Queue</button>
          )}
          {isCXUser() && (
            <button className={queueFilter === 'cx' ? 'active' : ''} onClick={() => setQueueFilter('cx')}>CxEx Review Queue</button>
          )}
        </div>

        {/* Main Table */}
        <div>
          {loading ? (
            <Loader text='Loading...' />
          ) : (
            <div className="mt-3">
              <div className="wf-searchbar" style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    placeholder="Search project, brand, contact…"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    style={{ maxWidth: 220, paddingRight: 30 }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        fetchData(1, searchText);
                      }
                    }}
                  />
                  {searchText && (
                    <span
                      onClick={() => { setSearchText(""); fetchData(1, ''); }}
                      style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", cursor: "pointer", color: "#888", fontSize: 14 }}
                      title="Clear"
                    >
                      ✖
                    </span>
                  )}
                </div>
                <select value={rowsPerPage} onChange={(e) => setRowsPerPage(Number(e.target.value))}>
                  <option value={15}>15 rows</option>
                  <option value={25}>25 rows</option>
                  <option value={50}>50 rows</option>
                  <option value={100}>100 rows</option>
                </select>
                <select
                  value={createdAtPreset ?? ''}
                  onChange={(e) => {
                    const preset = e.target.value as CreatedAtPreset;
                    if (!preset) return;
                    const { from, to } = presetRange(preset);
                    setCreatedFromFilter(from);
                    setCreatedToFilter(to);
                    setCreatedAtPreset(preset);
                  }}
                >
                  <option value="" disabled>Created At</option>
                  {(['today', 'week', 'month', 'year', 'all'] as CreatedAtPreset[]).map((preset) => (
                    <option key={preset} value={preset}>{PRESET_LABELS[preset]}</option>
                  ))}
                </select>
                {(brandFilter.length > 0 || recordTypeFilter.length > 0 || stageFilter.length > 0 || assigneeFilter.length > 0 || createdByFilter.length > 0 || dueStatusFilter.length > 0 || deliveredThisWeekFilter || createdFromFilter || createdToFilter) && (
                  <button
                    className="wf-btn"
                    onClick={() => {
                      setBrandFilter([]);
                      setRecordTypeFilter([]);
                      setStageFilter([]);
                      setAssigneeFilter([]);
                      setCreatedByFilter([]);
                      setDueStatusFilter([]);
                      setDeliveredThisWeekFilter(false);
                      setCreatedFromFilter('');
                      setCreatedToFilter('');
                      setCreatedAtPreset(null);
                    }}
                  >
                    Clear all filters
                  </button>
                )}
              </div>

              <div className="wf-card wf-tablewrap wf-fdc-table" style={{ maxHeight: 650, overflowY: 'auto' }}>
                <table>
                  <thead>
                    <tr>
                      <th>Sl No</th>
                      <th>Project</th>
                      <th>Brand
                        <ColumnFilterDropdown
                          label="Brand"
                          options={filterOptions.brands.map((b): ColumnFilterOption => ({ value: b, label: b }))}
                          selected={brandFilter}
                          onChange={setBrandFilter}
                        />
                      </th>
                      <th>Type
                        <ColumnFilterDropdown
                          label="Type"
                          options={RECORD_TYPE_OPTIONS}
                          selected={recordTypeFilter}
                          onChange={setRecordTypeFilter}
                        />
                      </th>
                      <th>Total Counts</th>
                      <th>Stage
                        <ColumnFilterDropdown
                          label="Stage"
                          options={STAGE_OPTIONS.map((s): ColumnFilterOption => ({ value: s, label: stageLabel(s) }))}
                          selected={stageFilter}
                          onChange={setStageFilter}
                        />
                      </th>
                      <th>Assignee
                        <ColumnFilterDropdown
                          label="Assignee"
                          options={filterOptions.assignees.map((a): ColumnFilterOption => ({ value: String(a.id), label: a.name }))}
                          selected={assigneeFilter}
                          onChange={setAssigneeFilter}
                          searchable
                        />
                      </th>
                      <th>Created By
                        <ColumnFilterDropdown
                          label="Created By"
                          options={filterOptions.created_by.map((c): ColumnFilterOption => ({ value: String(c.id), label: c.name }))}
                          selected={createdByFilter}
                          onChange={setCreatedByFilter}
                          searchable
                        />
                      </th>
                      <th>Created At
                        <DateRangeFilterDropdown
                          label="Created At"
                          from={createdFromFilter}
                          to={createdToFilter}
                          onChange={(from, to) => {
                            setCreatedFromFilter(from);
                            setCreatedToFilter(to);
                            setCreatedAtPreset(null);
                          }}
                        />
                      </th>
                      <th>Files</th>
                      <th>Due Date
                        <ColumnFilterDropdown
                          label="Due Date"
                          options={[{ value: 'met', label: 'Met' }, { value: 'overdue', label: 'Overdue' }]}
                          selected={dueStatusFilter}
                          onChange={setDueStatusFilter}
                        />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {downloads.length > 0 ? (
                      downloads.map((item, index) => (
                        <tr
                          key={item.request_id || item.id || index}
                          className="wf-rowlink"
                          onClick={() => window.open(`/data-centers/fdc/projects/${item.id}`, '_blank')}
                        >
                          <td data-label="Sl No">{((pageDetail.page || 1) - 1) * rowsPerPage + index + 1}</td>
                          <td data-label="Project">
                            <div style={{ fontWeight: 400, fontSize: 12 }}>
                              {item.summary ? capitalize(item.summary) : item.summary}
                            </div>
                            <div className="wf-mono" style={{ fontSize: 11, color: '#9ca3af' }}>{item.projectid}</div>
                          </td>
                          <td data-label="Brand">{item.brand_name}</td>
                          <td data-label="Type">{recordTypeLabel(item.record_type)}</td>
                          <td data-label="Total Counts">{item.number_of_contacts || '—'}</td>
                          <td data-label="Stage">
                            {item.stage ? (
                              <span className={stageChipClass(item.stage)}>{stageLabel(item.stage)}</span>
                            ) : (
                              <span style={{ color: '#9ca3af', fontSize: 11.5 }}>—</span>
                            )}
                          </td>
                          <td data-label="Assignee">{item.assignee_name || <span style={{ color: '#9ca3af' }}>Unassigned</span>}</td>
                          <td data-label="Created By">{item.created_by_name || '—'}</td>
                          <td data-label="Created At">{item.created_at ? new Date(item.created_at).toLocaleDateString('en-GB', { timeZone: 'America/Los_Angeles' }) : '—'}</td>
                          <td data-label="Files">{item.total_files ?? 0}</td>
                          <td data-label="Due Date"><span className={dueDateChipClass(item.sla_status)}>{formatDueDate(item.task_deadline_date)}</span></td>
                        </tr>
                      ))
                    ) : (
                      <tr><td colSpan={11}><div className="wf-empty-hint">No projects here.</div></td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {wizardOpen && (
          <FDCCreateWizard
            onClose={() => setWizardOpen(false)}
            onCreated={() => { setWizardOpen(false); fetchData(1, searchText); }}
          />
        )}

        {/* Pagination */}
        <Pagination
          page={pageDetail.page}
          total_pages={pageDetail.total_pages}
          total_records={pageDetail.total_records}
          page_size={pageDetail.page_size}
          onPageChange={(newPage) => fetchData(newPage, searchText)}
        />

        {/* Project overlay — unified stage pipeline, tasks/subtasks, chat, customer, files */}
        {workflowPopup.open && workflowPopup.item && (
          <ProjectDetailOverlay
            item={workflowPopup.item}
            onClose={() => {
              setWorkflowPopup({ open: false, item: null });
              // If this tab was opened straight at /projects/:id (a new-tab
              // project link), drop the id back off the URL on close so a
              // refresh lands on the plain list instead of reopening the popup.
              if (routeProjectId) window.history.replaceState({}, document.title, '/data-centers/fdc/projects');
            }}
            onChanged={() => fetchData(pageDetail.page || 1, searchText)}
          />
        )}

        {/* Error Popup */}
        {errorMsg && (
          <div className="popup-overlay">
            <div className="popup" style={{ textAlign: "center" }}>
              <p>{errorMsg}</p>
              <button onClick={() => setErrorMsg(null)} style={{
                backgroundColor: "#61A1D4",
                padding: "3px 10px",
                borderRadius: "7px",
                textAlign: "center",
                color: "white",
                border: "none",
                cursor: "pointer"
              }}>Close</button>
            </div>
          </div>
        )}

      </div>
      </div>

      <style>{`
                  .detail-row {
                      margin-bottom: 12px;
                      display: flex;
                      justify-content: space-between;
                      padding: 8px;
                      background-color: #f8f9fa;
                      border-radius: 5px;
                      border-left: 4px solid #61A1D4;
                  }
                  .detail-row strong {
                      color: #2c3e50;
                      min-width: 150px;
                  }
                  .detail-row span {
                      color: #495057;
                      text-align: right;
                      word-break: break-word;
                      max-width: 250px;
                  }
                  .popup-overlay {
                      position: fixed;
                      top: 0;
                      left: 0;
                      width: 100%;
                      height: 100%;
                      background-color: rgba(0, 0, 0, 0.5);
                      display: flex;
                      justify-content: center;
                      align-items: center;
                      z-index: 1000;
                  }
                  .popup {
                      background: white;
                      border-radius: 10px;
                      box-shadow: 0 5px 20px rgba(0,0,0,0.2);
                      animation: fadeIn 0.3s ease-out;
                  }
                  @keyframes fadeIn {
                      from { opacity: 0; transform: translateY(-20px); }
                      to { opacity: 1; transform: translateY(0); }
                  }
              `}</style>
    </MainLayout>
  );
};

export default FDCPage;